import { useEffect, type ReactNode } from 'react';
import { FolderX, Mail, X } from 'lucide-react';

/** Ademero's support address: the same one the web app names (apps/web/src/support-contact.ts). */
export const JOB_LIMIT_CONTACT = 'support@ademero.com';

/** The sentence the API's 409 `job_limit_reached` carries, for the limit it names. */
export function jobLimitWords(limit: number): string {
  return `This company has reached ${limit.toLocaleString('en-US')} jobs. Contact Ademero to raise the limit.`;
}

export interface JobLimitNoticeProps {
  limit: number;
  onClose: () => void;
}

/**
 * A new job refused because the company holds as many jobs as it may (migration 0299; Dustin 2026-10-10: a safety
 * cap of 1,000 against runaway or automated creation). Said in words with Ademero's address to write to, never as a
 * raw error, in the workbench's sheet language. Shown by the bridge's `jobLimit` when creating, importing or the tour
 * is refused 409 `job_limit_reached`.
 */
export function JobLimitNotice({ limit, onClose }: JobLimitNoticeProps): ReactNode {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const subject = encodeURIComponent(
    `Raise our Paige job limit (${limit.toLocaleString('en-US')})`,
  );
  return (
    <div className="paige-intake-backdrop" onClick={onClose}>
      <section
        className="paige-intake-sheet"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="paige-job-limit-title"
        aria-describedby="paige-job-limit-words"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="paige-intake-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <header className="paige-intake-head">
          <FolderX size={26} aria-hidden="true" />
          <h2 id="paige-job-limit-title">Job limit reached</h2>
          <p id="paige-job-limit-words">{jobLimitWords(limit)}</p>
        </header>
        <p>
          <a href={`mailto:${JOB_LIMIT_CONTACT}?subject=${subject}`}>
            <Mail size={16} aria-hidden="true" /> Email {JOB_LIMIT_CONTACT}
          </a>
        </p>
      </section>
    </div>
  );
}
